import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

/**
 * Path segment to i18n key mapping for breadcrumb labels.
 * Maps URL path segments to their corresponding translation keys.
 */
const SEGMENT_LABELS: Record<string, string> = {
  // Categories
  site: 'menu.siteSetup',
  applications: 'menu.applicationPhase',
  selection: 'menu.selectionPhase',
  enrollment: 'menu.enrollmentPhase',
  comms: 'menu.communication',
  reports: 'menu.reports',
  content: 'menu.contentAssets',
  settings: 'menu.settings',
  audit: 'menu.audit',
  release: 'menu.release',
  system: 'menu.system',
  // Items
  organizations: 'menu.organizations',
  exams: 'menu.exams',
  venues: 'menu.venues',
  'change-requests': 'menu.changeRequests',
  labels: 'menu.labels',
  search: 'menu.appSearch',
  accounts: 'menu.accountSearch',
  status: 'menu.appStatus',
  upload: 'menu.appUpload',
  results: 'menu.selectionResults',
  announcement: 'menu.resultAnnouncement',
  students: 'menu.enrollStudents',
  fees: 'menu.fees',
  payments: 'menu.payments',
  announcements: 'menu.announcements',
  messages: 'menu.messages',
  emails: 'menu.emails',
  documents: 'menu.documents',
  static: 'menu.static',
  batch: 'menu.batchDl',
  application: 'menu.appReports',
  files: 'menu.fileDl',
  users: 'menu.users',
  permissions: 'menu.permissions',
  basic: 'menu.basic',
  branding: 'menu.branding',
  profile: 'menu.profile',
  logs: 'menu.logs',
  preview: 'menu.preview',
  deploy: 'menu.deploy',
  tenants: 'menu.tenants',
  admins: 'menu.supportStaff',
  wizard: 'tenants.modal.addPartner',
  new: 'common.create',
};

export function AdminBreadcrumb() {
  const { t } = useTranslation();
  const location = useLocation();

  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Skip 'admin' as root if it's the first segment
  const segments = pathSegments[0] === 'admin' ? pathSegments.slice(1) : pathSegments;

  // Get label for a segment
  const getLabel = (segment: string): string => {
    // Suppress MEXT Codes / Corporate Numbers (13 chars)
    if (/^[A-Z0-9]{13}$/.test(segment)) {
      return t('common.details');
    }

    // Check explicit mapping first
    const key = SEGMENT_LABELS[segment];
    if (key) {
      return t(key);
    }
    // Fall back to menu.{segment} or raw segment
    const fallback = t(`menu.${segment}`, { defaultValue: '' });
    return fallback || segment;
  };

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {/* Tenant Root */}
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/">TOP</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />

        {/* Dynamic Segments */}
        {segments.map((segment, index) => {
          const path = `/${segments.slice(0, index + 1).join('/')}`;
          const isLast = index === segments.length - 1;
          const label = getLabel(segment);

          return (
            <div key={path} className="flex items-center gap-2">
              <BreadcrumbItem>
                {isLast ? (
                  <BreadcrumbPage>{label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={path}>{label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </div>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
